import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, CreditCard, MessageSquareText, Loader2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/lib/cart";
import { http, eur, errMsg, openRequest, setScrollLock, imgSrc } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const Line = ({ i, setQty }) => (
  <li className="flex gap-4 py-4" data-testid={`cart-item-${i.id}`}>
    <img src={imgSrc(i.image)} alt={i.name} className="h-20 w-20 rounded-2xl object-cover" />
    <div className="flex flex-1 flex-col">
      <p className="font-display text-lg">{i.name}</p>
      <p className="text-sm text-mist">{eur(i.price)}</p>
      <div className="mt-auto flex items-center gap-1">
        <button data-testid={`cart-item-quantity-minus-${i.id}`} onClick={() => setQty(i.id, i.qty - 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-fg/10 hover:bg-fg/10"><Minus className="h-3 w-3" /></button>
        <span className="w-7 text-center text-sm" data-testid={`cart-item-qty-${i.id}`}>{i.qty}</span>
        <button data-testid={`cart-item-quantity-plus-${i.id}`} onClick={() => setQty(i.id, i.qty + 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-fg/10 hover:bg-fg/10"><Plus className="h-3 w-3" /></button>
      </div>
    </div>
    <p className="font-display">{eur(i.price * i.qty)}</p>
  </li>
);

export const CartDrawer = () => {
  const { items, setQty, total, open, setOpen } = useCart();
  const [paying, setPaying] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setScrollLock(open);
  }, [open]);

  const pay = async () => {
    if (!user) {
      setOpen(false);
      toast("Accedi o crea un account per completare l'ordine");
      navigate("/account?next=checkout");
      return;
    }
    setPaying(true);
    try {
      const { data } = await http.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setPaying(false);
    }
  };
  const request = () => { setOpen(false); setTimeout(() => openRequest("preventivo", true), 300); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-lenis-prevent data-testid="cart-drawer" className="flex w-full flex-col border-fg/10 bg-[var(--ink)] sm:max-w-md">
        <SheetTitle className="font-display text-3xl">Carrello</SheetTitle>
        <SheetDescription className="text-mist">Prezzi IVA inclusa. Spedizione calcolata al pagamento.</SheetDescription>
        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center text-mist" data-testid="cart-empty">
            <ShoppingBag className="h-10 w-10" />
            <p>Il carrello è vuoto.</p>
          </div>
        ) : (
          <>
            <ul className="mt-4 flex-1 divide-y divide-fg/10 overflow-y-auto">
              {items.map((i) => <Line key={i.id} i={i} setQty={setQty} />)}
            </ul>
            <div className="border-t border-fg/10 pt-5">
              <div className="mb-5 flex items-end justify-between">
                <span className="text-mist">Totale</span>
                <span className="font-display text-3xl font-semibold" data-testid="cart-total">{eur(total)}</span>
              </div>
              <button onClick={pay} disabled={paying} data-testid="cart-checkout-stripe-button"
                className="shine flex w-full items-center justify-center gap-2 rounded-full bg-[var(--volt)] py-4 font-semibold text-[var(--ink)] disabled:opacity-60 active:scale-95">
                {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />} {user ? "Paga con carta" : "Accedi e paga con carta"}
              </button>
              <button onClick={request} data-testid="cart-request-form-button"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-fg/20 py-4 text-fg hover:bg-fg/5 active:scale-95">
                <MessageSquareText className="h-5 w-5" /> Richiedi un preventivo
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
